import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Search,
  Handshake,
  TrendingUp,
  KeyRound,
  type LucideIcon,
} from "lucide-react";
import { SectionHeading } from "@/components/site/SectionHeading";
import { cn } from "@/lib/utils";

interface Service {
  title: string;
  teaser: string;
  body: string;
  cta: string;
  to: string;
  icon: LucideIcon;
}

const SERVICES: Service[] = [
  {
    title: "Consulting",
    teaser: "Expert market guidance, tailored to you.",
    body: "We guide you through the Dubai property market with clarity and precision — where to buy, what to buy, and what the market is telling you right now.",
    cta: "Speak to an Advisor",
    to: "/contact",
    icon: Compass,
  },
  {
    title: "Property Search & Viewings",
    teaser: "Hand-picked properties, viewings on your schedule.",
    body: "We hand-pick properties that genuinely match your brief and arrange viewings around your schedule. Every home we present, we know personally.",
    cta: "Start Your Search",
    to: "/contact?intent=buy",
    icon: Search,
  },
  {
    title: "Deal Management",
    teaser: "Every detail managed, start to finish.",
    body: "From negotiation and contract terms to financing connections and legal coordination, we manage every detail so your transaction completes seamlessly.",
    cta: "Enquire Now",
    to: "/contact",
    icon: Handshake,
  },
  {
    title: "Investment Advisory",
    teaser: "The right opportunities, identified for you.",
    body: "Whether you're building a portfolio or making your first investment in Dubai, we identify the right opportunities, analyse the returns, and guide you to a decision with confidence.",
    cta: "Explore Opportunities",
    to: "/contact?intent=off-plan",
    icon: TrendingUp,
  },
  {
    title: "After-Sales",
    teaser: "We stay with you, long after the keys.",
    body: "Our relationship doesn't end at completion. Move-in support, maintenance connections, community introductions — we remain your point of contact long after the deal is done.",
    cta: "Learn More",
    to: "/contact",
    icon: KeyRound,
  },
];

export function Practice() {
  return (
    <section className="border-t border-[var(--line)]">
      <div className="container-wide py-12 sm:py-16 md:py-24 short:py-10">
        <SectionHeading
          eyebrow="How We Help"
          size="xl"
          title="Real Estate, The Way It Should Be."
          body="Five ways we make buying, selling and investing in Dubai feel effortless — from first conversation to long after the keys change hands."
        />

        <ul className="mt-10 flex flex-wrap justify-center gap-5 sm:mt-12">
          {SERVICES.map((service) => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function ServiceCard({ service }: { service: Service }) {
  const Icon = service.icon;
  return (
    <li className="basis-full sm:basis-[calc(50%-10px)] lg:basis-[calc(33.333%-14px)]">
      <Link
        to={service.to}
        className="focus-ring group relative flex h-full flex-col overflow-hidden border border-[var(--line-strong)] bg-[var(--surface)] p-6 transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-[var(--gold-bright)] sm:p-7"
      >
        <span className="flex size-11 flex-none items-center justify-center rounded-[2px] bg-[var(--surface-2)] text-[var(--gold-bright)] transition-colors duration-200 group-hover:bg-[var(--navy)] group-hover:text-[var(--cream)]">
          <Icon className="size-5" strokeWidth={1.5} aria-hidden />
        </span>

        <h3
          className="mt-5 font-display text-[22px] leading-tight text-[var(--text)] sm:text-[25px]"
          style={{ fontVariationSettings: '"opsz" 36' }}
        >
          {service.title}
        </h3>
        <p className="mt-2 text-[14px] font-medium leading-[1.5] text-[var(--text)]">
          {service.teaser}
        </p>
        <p className="mt-3 text-[13.5px] leading-[1.6] text-[var(--text-soft)]">
          {service.body}
        </p>

        <span className="mt-6 inline-flex items-center gap-1.5 pt-2 text-[12px] uppercase tracking-[0.16em] text-[var(--gold-bright)]">
          {service.cta}
          <ArrowRight
            className="size-3.5 transition-transform duration-200 group-hover:translate-x-1"
            aria-hidden
          />
        </span>

        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-[var(--gold-bright)] transition-transform duration-300 ease-out group-hover:scale-x-100"
        />
      </Link>
    </li>
  );
}
